import { useQuery } from "@apollo/client";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";

import { CAMPAIGN_EVENT_RESULTS, CAMPAIGN_RESULTS } from "../gql/queries/inviteCampaigns";
import { Spacing } from "../types/enum";

interface EventResult {
  eventId: string;
  title: string;
  /** Epoch milliseconds — see the note on ResultWeek.weekStarting. */
  startDate: number;
  invited: number;
  saidYes: number;
  came: number;
  totalThere: number;
}

/**
 * The events behind one week, fetched only when that week is opened.
 *
 * A week reading as average can be one strong event and three that reached
 * nobody, which the weekly figure alone hides.
 */
const WeekEvents: React.FC<{ campaign: string; weekStarting: number }> = ({
  campaign,
  weekStarting,
}) => {
  const { loading, data } = useQuery(CAMPAIGN_EVENT_RESULTS, {
    variables: { campaign, weekStarting },
    fetchPolicy: "cache-first",
  });

  const events: EventResult[] = data?.adminCampaignEventResults ?? [];

  if (loading) {
    return (
      <Box p={Spacing.s}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box p={Spacing.s}>
        <Typography variant="caption" color="textSecondary">
          No events found for this week.
        </Typography>
      </Box>
    );
  }

  return (
    <Table size="small" sx={{ my: Spacing.s }}>
      <TableHead>
        <TableRow>
          <TableCell>Event</TableCell>
          <TableCell>Held</TableCell>
          <TableCell align="right">Invited</TableCell>
          <TableCell align="right">Said yes</TableCell>
          <TableCell align="right">Came</TableCell>
          <TableCell align="right">Total there</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {events.map((event) => (
          <TableRow key={event.eventId}>
            <TableCell sx={{ maxWidth: 360 }}>
              <Typography variant="body2">{event.title}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="caption" color="textSecondary" noWrap>
                {format(new Date(event.startDate), "EEE d MMM, HH:mm")}
              </Typography>
            </TableCell>
            <TableCell align="right">{event.invited}</TableCell>
            <TableCell align="right">
              <Typography
                variant="body2"
                component="span"
                color={event.invited > 0 && event.saidYes === 0 ? "warning.main" : "textPrimary"}
              >
                {event.saidYes}
              </Typography>
            </TableCell>
            <TableCell align="right">{event.came}</TableCell>
            <TableCell align="right">{event.totalThere}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface ResultWeek {
  /**
   * Epoch milliseconds, not an ISO string — the API's Date scalar serialises
   * with `getTime()`. Reach for `new Date(...)` here, never `parseISO`, which
   * silently returns an invalid date and makes `format` throw.
   */
  weekStarting: number;
  events: number;
  invited: number;
  saidYes: number;
  came: number;
  totalThere: number;
  partial: boolean;
}

interface Props {
  campaign: string;
  /** What this campaign counts as its own events, in plain words. */
  scope: string;
}

interface Period {
  key: string;
  label: string;
  from: number;
  to: number;
  /** Shown under the summary figures, so the period is never ambiguous. */
  covers: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The periods worth asking about: recent rolling windows for "how are we
 * doing", and whole calendar months for "how did June go".
 *
 * Months are built from today backwards rather than hard-coded, so the list
 * stays right without anyone maintaining it.
 */
const buildPeriods = (): Period[] => {
  const now = new Date();
  const rolling = [14, 30, 56, 90].map((days) => ({
    key: `last-${days}`,
    label: `Last ${days} days`,
    from: now.getTime() - days * DAY_MS,
    to: now.getTime(),
    covers: `${format(new Date(now.getTime() - days * DAY_MS), "d MMM")} to today`,
  }));

  const months: Period[] = [];
  for (let back = 0; back < 6; back += 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - back + 1, 1);
    months.push({
      key: `month-${format(start, "yyyy-MM")}`,
      label: format(start, "MMMM yyyy"),
      from: start.getTime(),
      to: end.getTime(),
      covers:
        back === 0
          ? `${format(start, "d MMM")} to today — this month isn't finished`
          : `${format(start, "d MMM")} to ${format(new Date(end.getTime() - DAY_MS), "d MMM yyyy")}`,
    });
  }

  return [...rolling, ...months];
};

/** A rate, or a dash when there's nothing to divide by. */
const rate = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—";

const CampaignResults: React.FC<Props> = ({ campaign, scope }) => {
  // Which week is open. One at a time — this is a drill-down, not a sprawl.
  const [expanded, setExpanded] = useState<number | null>(null);

  const periods = useMemo(buildPeriods, []);
  const [periodKey, setPeriodKey] = useState<string>("last-56");
  const period = periods.find((entry) => entry.key === periodKey) ?? periods[2];

  const { loading, data } = useQuery(CAMPAIGN_RESULTS, {
    variables: { campaign, from: period.from, to: period.to },
    fetchPolicy: "network-only",
  });

  const weeks: ResultWeek[] = data?.adminCampaignResults ?? [];

  // Best and worst are only meaningful against finished weeks — the current one
  // is still filling up, and would otherwise take the crown most Mondays.
  const settled = weeks.filter((week) => !week.partial && week.invited > 0);
  const rates = settled.map((week) => week.saidYes / week.invited);
  const best = rates.length ? Math.max(...rates) : null;
  const worst = rates.length > 1 ? Math.min(...rates) : null;

  // The summary covers the whole period she picked, part-week included — asking
  // for the last 14 days and being shown only the finished weeks would quietly
  // drop half of it. Best and worst above still ignore the part week, where a
  // half-collected rate genuinely isn't comparable.
  const totals = weeks.reduce(
    (sum, week) => ({
      invited: sum.invited + week.invited,
      saidYes: sum.saidYes + week.saidYes,
      came: sum.came + week.came,
    }),
    { invited: 0, saidYes: 0, came: 0 },
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.l}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={Spacing.m}>
      <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Period"
          value={periodKey}
          onChange={(event) => {
            setPeriodKey(event.target.value);
            // The open week belongs to the old period; keep nothing stale.
            setExpanded(null);
          }}
          sx={{ minWidth: 200 }}
        >
          {periods.map((entry) => (
            <MenuItem key={entry.key} value={entry.key}>
              {entry.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary">
          {period.covers}
        </Typography>
      </Box>

      <Alert severity="info" variant="outlined">
        <Typography variant="body2" fontWeight={600} gutterBottom>
          What these numbers cover
        </Typography>
        <Typography variant="caption" component="p" color="textSecondary">
          <strong>Events:</strong> {scope} Only events already held, since attendance doesn&apos;t
          exist until afterwards — so this never describes the same events as the Invite tab.
        </Typography>
        <Typography variant="caption" component="p" color="textSecondary" mt={Spacing.xs}>
          <strong>Members:</strong> everyone holding an invitation she didn&apos;t create by signing
          up herself, including those who never replied — that&apos;s what makes the rate honest
          rather than flattering.
        </Typography>
        <Typography variant="caption" component="p" color="textSecondary" mt={Spacing.xs}>
          <strong>Whose invitations:</strong> an invitation doesn&apos;t record who sent it, so this
          counts <em>every</em> invitation to these events — this campaign, another campaign,
          another admin, the host. Campaign sends now go to the outreach log, which will make the
          distinction answerable.
        </Typography>
      </Alert>

      {totals.invited > 0 && (
        <Box display="flex" gap={Spacing.l} flexWrap="wrap">
          <Box>
            <Typography variant="h4" fontWeight={300}>
              {rate(totals.saidYes, totals.invited)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              said yes · {totals.saidYes} of {totals.invited} invited
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={300}>
              {rate(totals.came, totals.invited)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              came · {totals.came} of {totals.invited} invited
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={300}>
              {rate(totals.came, totals.saidYes)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              of those who said yes turned up
            </Typography>
          </Box>
        </Box>
      )}

      <Card variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week</TableCell>
                <TableCell align="right">Events</TableCell>
                <TableCell align="right">Invited</TableCell>
                <TableCell align="right">Said yes</TableCell>
                <TableCell align="right">Came</TableCell>
                <TableCell align="right">Said yes %</TableCell>
                <TableCell align="right">Total there</TableCell>
                <TableCell>Share of the room</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="textSecondary">
                      No events for this campaign in the last 8 weeks.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {weeks.map((week) => {
                const yesRate = week.invited > 0 ? week.saidYes / week.invited : null;
                const share = week.totalThere > 0 ? (week.came / week.totalThere) * 100 : 0;
                const isBest = !week.partial && yesRate !== null && yesRate === best;
                const isWorst = !week.partial && yesRate !== null && yesRate === worst;

                const isOpen = expanded === week.weekStarting;

                return (
                  <React.Fragment key={week.weekStarting}>
                    <TableRow
                      hover
                      onClick={() => setExpanded(isOpen ? null : week.weekStarting)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={
                            isOpen ? "Hide this week's events" : "Show this week's events"
                          }
                          sx={{ mr: Spacing.xs }}
                        >
                          {isOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Typography variant="body2" fontWeight={600} component="span">
                          {format(new Date(week.weekStarting), "d MMM")}
                        </Typography>
                        {week.partial && (
                          <Typography variant="caption" color="textSecondary" ml={Spacing.xs}>
                            part week
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{week.events}</TableCell>
                      <TableCell align="right">{week.invited}</TableCell>
                      <TableCell align="right">{week.saidYes}</TableCell>
                      <TableCell align="right">{week.came}</TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            isBest
                              ? "Best finished week"
                              : isWorst
                                ? "Weakest finished week"
                                : week.partial
                                  ? "Still running — the rate will move"
                                  : ""
                          }
                        >
                          <Typography
                            variant="body2"
                            component="span"
                            fontWeight={isBest || isWorst ? 600 : 400}
                            color={
                              week.partial
                                ? "textSecondary"
                                : isBest
                                  ? "success.main"
                                  : isWorst
                                    ? "warning.main"
                                    : "textPrimary"
                            }
                          >
                            {rate(week.saidYes, week.invited)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{week.totalThere}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={Spacing.s}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(share, 100)}
                            sx={{ width: 56, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {Math.round(share)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit timeout="auto">
                          {isOpen && (
                            <WeekEvents campaign={campaign} weekStarting={week.weekStarting} />
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Typography variant="caption" color="textSecondary">
        &ldquo;Share of the room&rdquo; is how many of everyone present came from an invitation.
        Attendance counts both records of turning up — the conversation, and the bare Zoom join.
      </Typography>
    </Box>
  );
};

export default CampaignResults;
